import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Container, Nav, Spinner, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiGet } from '../../services/api';
import AdminAnalyticsTab from './AdminAnalyticsTab';
import AdminCompaniesTab from './AdminCompaniesTab';
import AdminDevMailboxTab from './AdminDevMailboxTab';
import AdminPendingJobsTab from './AdminPendingJobsTab';
import AdminPricingTab from './AdminPricingTab';
import AdminSkillsTab from './AdminSkillsTab';
import AdminSecurityTab from './AdminSecurityTab';

export default function AdminPanel() {
  const { token, user, authLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('pending');
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState('');
  const [showDevMailbox, setShowDevMailbox] = useState(false);

  const handleAuthError = useCallback(
    (res) => {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return true;
      }
      if (res.status === 403) {
        setAccessDenied(true);
        return true;
      }
      return false;
    },
    [navigate]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!token || !user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.role !== 'admin') {
      if (user.role === 'candidate') {
        navigate('/dashboard', { replace: true });
        return;
      }
      if (user.role === 'company') {
        navigate('/company/dashboard', { replace: true });
        return;
      }
      setAccessDenied(true);
      return;
    }
  }, [authLoading, token, user, navigate]);

  useEffect(() => {
    if (authLoading || !token || user?.role !== 'admin') return;

    let cancelled = false;

    async function loadConfig() {
      try {
        const configRes = await apiGet('/api/v1/admin/config', token);
        if (cancelled) return;
        if (handleAuthError(configRes)) return;
        if (configRes.ok) {
          const configData = await configRes.json();
          setShowDevMailbox(configData.data?.show_dev_mailbox === true);
        }
      } catch {
        if (!cancelled) {
          setShowDevMailbox(false);
        }
      }
    }

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, [authLoading, token, user, handleAuthError]);

  if (authLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" data-testid="rwm-admin-loading">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container className="py-5">
        <Alert variant="danger" data-testid="rwm-admin-access-denied">
          Access denied. This page is for admin accounts only.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4 rwm-admin">
      <h1 className="h3 mb-4 rwm-admin__title">Admin Panel</h1>

      {error ? (
        <Alert variant="danger" className="mb-4" data-testid="rwm-admin-error">
          {error}
        </Alert>
      ) : null}

      <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
        <Nav variant="tabs" className="rwm-admin__nav mb-4">
          <Nav.Item>
            <Nav.Link eventKey="pending" data-testid="rwm-admin-tab-pending">
              Pending Jobs
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="companies" data-testid="rwm-admin-tab-companies">
              Companies
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="analytics" data-testid="rwm-admin-tab-analytics">
              Analytics
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="pricing" data-testid="rwm-admin-tab-pricing">
              Pricing
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="skills" data-testid="rwm-admin-tab-skills">
              Skills
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="security" data-testid="rwm-admin-tab-security">
              Security
            </Nav.Link>
          </Nav.Item>
          {showDevMailbox ? (
            <Nav.Item>
              <Nav.Link eventKey="devmailbox" data-testid="rwm-admin-tab-devmailbox">
                Dev Mailbox
              </Nav.Link>
            </Nav.Item>
          ) : null}
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="pending">
            <AdminPendingJobsTab
              token={token}
              setError={setError}
              handleAuthError={handleAuthError}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="companies">
            <AdminCompaniesTab
              token={token}
              setError={setError}
              handleAuthError={handleAuthError}
              isActive={activeTab === 'companies'}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="analytics">
            <AdminAnalyticsTab token={token} handleAuthError={handleAuthError} />
          </Tab.Pane>
          <Tab.Pane eventKey="pricing">
            <AdminPricingTab
              token={token}
              isActive={activeTab === 'pricing'}
              handleAuthError={handleAuthError}
              setError={setError}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="skills">
            <AdminSkillsTab
              token={token}
              isActive={activeTab === 'skills'}
              handleAuthError={handleAuthError}
              setError={setError}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="security">
            <AdminSecurityTab
              token={token}
              handleAuthError={handleAuthError}
            />
          </Tab.Pane>
          {showDevMailbox ? (
            <Tab.Pane eventKey="devmailbox" unmountOnExit>
              <AdminDevMailboxTab
                token={token}
                isActive={activeTab === 'devmailbox'}
                handleAuthError={handleAuthError}
              />
            </Tab.Pane>
          ) : null}
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
}
